import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from './useApi';

// The workflow data behind GRC Security Insights controls: one scope-filtered read of the GRC and
// Defender XDR risks (the types behind a control's grcRiskId), never one request per control.
// Closed and accepted risks are included: their controls show a read-only or limited status line.
const RISKS_URL = '/unifiedrisks/details?riskTypes=GRC,DefenderXDR';
export const AUDIT_FINDING_SOURCE = 'AuditFinding';
// A NERC patch's own risk (CIP-007 R2): worked in the patch view, never a control's risk.
export const NERC_PATCH_SOURCE = 'NercPatch';
const NOT_CONTROL_RISK_SOURCES = [AUDIT_FINDING_SOURCE, NERC_PATCH_SOURCE];

// The server's open statuses (UnifiedRiskRepository openOnly): identified or in progress.
const OPEN_STATUSES = ['identified', 'in_progress'];
export const isOpenRisk = (risk) => OPEN_STATUSES.includes(String(risk?.status || '').toLowerCase());
export const isClosedRisk = (risk) => String(risk?.status || '').toLowerCase() === 'closed';

export const useGrcRiskWorkflow = (enabled) => {
    const apiCall = useApi();
    const [risks, setRisks] = useState(null); // null until the first read completes
    const [failed, setFailed] = useState(false);
    const seq = useRef(0);

    // A newer read wins: an older one resolving late never overwrites it.
    const reload = useCallback(async () => {
        const mine = ++seq.current;
        try {
            const data = await apiCall('GET', RISKS_URL);
            if (mine !== seq.current) return;
            // An audit finding's or a NERC patch's risk has no control and is listed with its finding or
            // patch, never as a control's risk.
            setRisks(Array.isArray(data) ? data.filter(r => !NOT_CONTROL_RISK_SOURCES.includes(r.riskSource)) : []);
            setFailed(false);
        } catch {
            if (mine !== seq.current) return;
            setFailed(true);
        }
    }, [apiCall]);

    useEffect(() => {
        if (enabled && risks === null) reload();
    }, [enabled, risks, reload]);

    const byId = useMemo(() => new Map((risks || []).map(r => [String(r.id), r])), [risks]);

    // Open risks per SecurityControl id: a control normally links one risk (its newest), but if two
    // are open, both are shown rather than one picked silently.
    const openByControlId = useMemo(() => {
        const map = new Map();
        (risks || []).forEach(r => {
            if (r.securityControlId == null || !isOpenRisk(r) || r.riskType !== 'GRC') return;
            const key = String(r.securityControlId);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        });
        return map;
    }, [risks]);

    // The risks a control's workflow line shows: every open risk of the control when it has more than
    // one, otherwise the risk the control links (grcRiskId), if this user may read it.
    const risksForControl = useCallback((control) => {
        // Defender XDR rows are synthetic controls with negative ids, one risk each.
        const open = control.id > 0 ? openByControlId.get(String(control.id)) : null;
        if (open && open.length > 1) return open;
        const linked = control.grcRiskId ? byId.get(String(control.grcRiskId)) : null;
        return linked ? [linked] : [];
    }, [byId, openByControlId]);

    // A control with a risk whose workflow is still on its way: the read is wanted and has not
    // finished, so the control shows "Loading risk workflow…" rather than no workflow at all.
    const isPending = useCallback((control) =>
        !!enabled && risks === null && !failed && !!control?.grcRiskId, [enabled, risks, failed]);

    // risks: every risk of the read (the Issues view lists the open ones).
    return { loaded: risks !== null, failed, risks: risks || [], risksForControl, isPending, reload };
};
